import { apiGet, apiPatch, apiPost, apiPut, apiDelete } from './client';
import { publishReleaseFlagDeferral, type DoneDeferral } from '../releaseFlagDeferral';

// ===== Phase 45 — MyTasks (assignee-scoped) + claim flow =====
//
// Backend contract: GET /api/tasks/mine + GET /api/tasks/unassigned both
// return TaskRowDto[] as shipped by Plan 45-03 (see
// backend/src/api/schemas/taskAssignee.ts:67-86 — taskRowSchema). Optional
// fields (sla, signals, milestoneName) are present on the wire as nullable;
// extras (estimateHours, ageDays, billable, etc.) flagged optional are
// graceful-degradation hooks for v7.1 backend extensions per UI-SPEC §Out-of-
// Scope. The UI renders defensively when a field is absent.

export interface MyTaskDto {
  taskId: number;
  taskName: string;
  taskCode: string | null;
  status: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low' | string;
  assigneeId: number | null;
  projectId: number;
  projectName: string;
  customerId: number | null;
  milestoneId: number | null;
  milestoneName: string | null;
  tierId: number | null;
  /** Plan 50-06 — tier object populated by Plan 50-02 backend extension. */
  tier?: { id: number; orderIndex: number; name: string } | null;
  taskStateName: string | null;
  /**
   * Petra UAT 2026-05-19 — task_states.color hex (e.g. "#FC8332"). When
   * present, MyTaskRow uses it for the row tint so the My Tasks view stays
   * visually consistent with the project's kanban board (which reads the
   * same task_states.color via BoardColumn.color). null when the task has
   * no task_state row (legacy data) — UI falls back to the neutral row.
   */
  taskStateColor?: string | null;
  // Phase 86 (86-01/86-02) — always sent by every producer of TaskRowDto
  // (getMyTasksByAssignee, listUnassignedClaimable, listEligibleQueueTasks)
  // and declared in taskAssignee.ts's response schema, so these are no
  // longer graceful-degradation optionals. Drives the "Compost" row
  // (86-03): the rot meter, the zone spine, the LOGGED/CLIENT/DUE cells and
  // the meta-line chips.
  /** Same clock the kanban board reads — max(task edit, comment, entry). */
  lastActivityAt: string | null;
  /** Done/Archived/Closed/Deleted — the rot clock stops for these. */
  isTerminal: boolean;
  /** D-DUE (86-CONTEXT) — the task's milestone endDate, or null. */
  dueAt: string | null;
  /** Summed Entry.hours; always a number, 0 when nothing is logged yet. */
  loggedHours: number;
  /** Task.plannedHours; null when the task has no estimate. */
  estimatedHours: number | null;
  customerName: string | null;
  isExternal: boolean;
  /** Total unread comment count (not just @mentions). */
  unreadCommentCount: number;
  hasMention: boolean;
  /** Within-zone pressure sort input. null = "no SLA", 0 = "just started". */
  slaReactionPct: number | null;
  // Optional UI-only fields. Backend may add these in v7.1; until then the
  // FE treats them as null/undefined (graceful degradation).
  budget?: { hoursLogged: number; hoursEstimated: number | null } | null;
  sla?: { rPct: number; rState: 'stopped' | 'running'; xPct: number; xState: 'stopped' | 'running' } | null;
  dueDate?: string | null;
  billable?: boolean;
  projectKind?: 'standard' | 'sla' | 'internal' | string;
  customerColorHex?: string | null;
}

/**
 * Plan 45-07 (D-03) — claimable feed item. Same shape as MyTaskDto plus
 * estimate + age decoration; both are optional until backend ships them.
 */
export interface UnassignedTaskDto extends MyTaskDto {
  estimateHours?: number | null;
  ageDays?: number | null;
}

/**
 * Plan 45-06 — fetch the assignee-scoped MyTasks list.
 * Maps to GET /api/tasks/mine (rewritten by Plan 45-03).
 * Petra UAT 2026-05-19 — pass `includeDone=true` to opt into the
 * "Show done" toggle (legacy `status='Done'` rows surface).
 */
export async function fetchMyTasks(includeDone = false): Promise<MyTaskDto[]> {
  const qs = includeDone ? '?includeDone=true' : '';
  const response = await apiGet<{ data: MyTaskDto[] }>(`/tasks/mine${qs}`);
  return response.data;
}

/**
 * Plan 45-07 — fetch unassigned claimable tasks scoped to caller's projects.
 * Maps to GET /api/tasks/unassigned.
 */
export async function fetchUnassignedTasks(): Promise<UnassignedTaskDto[]> {
  const response = await apiGet<{ data: UnassignedTaskDto[] }>('/tasks/unassigned');
  return response.data;
}

// ===== MKS-144 — ticket ref → projectId locate (full-page ticket view) =====

export interface TaskLocation {
  taskId: number;
  projectId: number;
}

/**
 * MKS-144 — resolve a ticket ref (numeric id OR ticket code, e.g. MKS-144)
 * to the project it lives on. Backs GET /api/tasks/:taskId/locate
 * (backend/src/api/routes/taskLocateRoutes.ts). The frontend full-page
 * ticket view (TaskDetailPage) uses the resolved projectId to reuse the
 * existing board query — no new single-task DTO exists.
 */
export async function fetchTaskLocation(ref: string): Promise<TaskLocation> {
  const response = await apiGet<{ data: TaskLocation }>(
    `/tasks/${encodeURIComponent(ref)}/locate`,
  );
  return response.data;
}

// ===== 260727-gob — "My reports" (caller-scoped, across all projects) =====

export interface MyReport {
  id: number;
  code: string;
  name: string;
  projectId: number;
  projectName: string;
  stateName: string;
  createdAt: string;
}

/**
 * 260727-gob — fetch tasks created by the current user, newest first.
 * Maps to GET /api/tasks/my-reports. Any authenticated internal user may
 * call this; the backend scopes strictly to createdById = currentUser.id.
 */
export async function getMyReports(): Promise<MyReport[]> {
  const response = await apiGet<{ data: MyReport[] }>('/tasks/my-reports');
  return response.data;
}

/**
 * Plan 45-07 (D-18) — atomic claim. 409 surfaces as ApiClientError with
 * statusCode 409 ("Task is already assigned") which the optimistic
 * useClaimTask hook reverts and toasts.
 */
export async function claimTask(taskId: number): Promise<{ task: MyTaskDto; transitioned: boolean }> {
  const response = await apiPost<{ data: { task: MyTaskDto; transitioned: boolean } }>(
    `/tasks/${taskId}/claim`
  );
  return response.data;
}

/**
 * Plan 45-03 (D-02) — reassign / drop assignee. Admin can override anywhere;
 * project PM + assignees can reassign within the project. Pass assigneeId=null
 * to drop the assignee back to the "free market" pool. Server returns the
 * fresh MyTaskDto so the FE can patch caches without a refetch.
 */
/**
 * Quick task 260810-l18 (Task 3) — the INTERNAL author of a finished task
 * approves or reopens it. Mirrors submitAuthorReviewExternal (externalBoard.ts)
 * but through the internal apiPost client. Reason is required by the backend
 * only for 'reopened'.
 */
export async function submitAuthorReview(
  taskId: number,
  decision: 'approved' | 'reopened',
  reason?: string,
): Promise<void> {
  await apiPost<{ data: unknown }>(`/tasks/${taskId}/author-review`, {
    decision,
    ...(reason ? { reason } : {}),
  });
}

export async function patchTaskAssignee(
  taskId: number,
  assigneeId: number | null,
  options?: {
    externalAssigneeId?: number | null;
    assigneeKind?: 'user' | 'external_user';
  },
): Promise<{ task: MyTaskDto }> {
  const response = await apiPatch<{ data: { task: MyTaskDto } }>(
    `/tasks/${taskId}/assignee`,
    {
      assigneeId: options?.assigneeKind === 'external_user' ? null : assigneeId,
      externalAssigneeId: options?.externalAssigneeId ?? null,
      assigneeKind: options?.assigneeKind ?? 'user',
    },
  );
  return response.data;
}

// ===== Legacy manager/tasks types (kept; consumed by manager TaskManagementPage) =====

export interface TaskWithStats {
  id: number;
  name: string;
  code: string | null;
  status: string | null;
  origin: string | null;
  projectId: number;
  entryCount: number;
  totalHours: number;
  budget: number | null;
  budgetSpent: number | null;
  hardLimit: boolean;
  plannedHours: number | null;
  taskStateId?: number | null;
  taskState?: { id: number; name: string; color: string; ordering: number } | null;
}

export interface ProjectWithTasks {
  id: number;
  name: string | null;
  fullName: string | null;
  state: string | null;
  parentId: number | null;
  tasks: TaskWithStats[];
}

export interface MoveTasksResult {
  moved: number[];
  failed: { id: number; reason: string }[];
}

export interface CreateTaskInput {
  projectId: number;
  name: string;
  code?: string;
  status?: string;
  budget?: number | null;
  hardLimit?: boolean;
  plannedHours?: number | null;
  tierId?: number | null; // Phase 49 Plan 49-05 — RC-01 gate: required for SLA Task projects
}

export interface UpdateTaskInput {
  name?: string;
  status?: string;
  budget?: number | null;
  hardLimit?: boolean;
  plannedHours?: number | null;
}

export interface BulkDeleteResult {
  deleted: number[];
  archived: number[];
  failed: { id: number; reason: string }[];
}

export interface MergeTasksResult {
  merged: number[];
  entriesMoved: number;
  failed: { id: number; reason: string }[];
}

// ===== API Functions =====

/**
 * Get tasks grouped by projects managed by the current user
 */
export async function getManagerTasks(scope?: 'all'): Promise<ProjectWithTasks[]> {
  const url = scope === 'all' ? '/manager/tasks?scope=all' : '/manager/tasks';
  const response = await apiGet<{ data: ProjectWithTasks[] }>(url);
  return response.data;
}

/**
 * Bulk move tasks to a target project
 */
export async function bulkMoveTasks(
  taskIds: number[],
  targetProjectId: number
): Promise<MoveTasksResult> {
  const response = await apiPost<{ data: MoveTasksResult }>('/manager/tasks/move', {
    taskIds,
    targetProjectId,
  });
  return response.data;
}

/**
 * Create a new task
 */
export async function createTaskApi(data: CreateTaskInput): Promise<TaskWithStats> {
  const response = await apiPost<{ data: TaskWithStats }>('/manager/tasks', data);
  return response.data;
}

/**
 * Update a task
 */
export async function updateTaskApi(id: number, data: UpdateTaskInput): Promise<TaskWithStats> {
  const response = await apiPut<{ data: TaskWithStats }>('/manager/tasks/' + id, data);
  return response.data;
}

/**
 * Delete a task
 */
export async function deleteTaskApi(id: number): Promise<{ action: 'deleted' | 'archived' }> {
  const response = await apiDelete<{ data: { action: 'deleted' | 'archived' } }>('/manager/tasks/' + id);
  return response.data;
}

/**
 * Bulk delete tasks
 */
export async function bulkDeleteTasksApi(taskIds: number[]): Promise<BulkDeleteResult> {
  const response = await apiPost<{ data: BulkDeleteResult }>('/manager/tasks/bulk-delete', { taskIds });
  return response.data;
}

/**
 * Merge source tasks into a target task
 */
export async function mergeTasksApi(
  sourceTaskIds: number[],
  targetTaskId: number
): Promise<MergeTasksResult> {
  const response = await apiPost<{ data: MergeTasksResult }>('/manager/tasks/merge', {
    sourceTaskIds,
    targetTaskId,
  });
  return response.data;
}

/**
 * Change a task's state (Phase 19, D-20; Phase 18 API-03).
 *
 * PATCH /api/projects/:projectId/tasks/:taskId
 * Body: { taskStateId: number }    — taskStateId-only; legacy `{ status }`
 *                                    is rejected by the server with
 *                                    400 LEGACY_STATUS_SHAPE_REJECTED.
 * 200 Response: { data: { taskId, taskStateId, deferred } } — `deferred` is
 * non-null when a Done move was deferred by a release flag (Phase 120, D-120-23);
 * the task did NOT move, and it is published to the global deferral notice.
 */
export async function setTaskState(
  projectId: number,
  taskId: number,
  taskStateId: number
): Promise<{ taskId: number; taskStateId: number; deferred?: DoneDeferral | null }> {
  const response = await apiPatch<{
    data: { taskId: number; taskStateId: number; deferred?: DoneDeferral | null };
  }>(`/projects/${projectId}/tasks/${taskId}`, { taskStateId });
  if (response.data.deferred) publishReleaseFlagDeferral(response.data.deferred);
  return response.data;
}
